import { DateTime } from "luxon";
import {
  required,
  number,
  minValue,
  maxValue,
  email,
  regex,
  minLength,
  maxLength,
} from "react-admin";
import { isMorfyFund, isRestaurantFund } from "./DiscountCouponUtil";
import { RESTAURANT_SERVICE_TYPES } from "../enums/RestaurantServiceTypes";
import { fractionToPercentInput, isEmptyValue } from "./NumberUtils";

export const MAX_MORFY_SPLIT_PERCENTAGE = 0.9;
export const MAX_MORFY_SPLIT_PERCENTAGE_INPUT = fractionToPercentInput(
  MAX_MORFY_SPLIT_PERCENTAGE
);

const validateBelowMaxMorfySplit = (value) =>
  isEmptyValue(value) || Number(value) < MAX_MORFY_SPLIT_PERCENTAGE
    ? undefined
    : `La comisión debe ser menor a ${MAX_MORFY_SPLIT_PERCENTAGE_INPUT}%`;

const validateInteger = (value) =>
  isEmptyValue(value) || Number.isInteger(Number(value))
    ? undefined
    : "Debe ser un número entero";

export const PHONE_REGEX = /^(\+)(\d{1,3})(\d{7,14})$/;
export const MAX_PRODUCT_DESCRIPTION_LENGTH = 400;
/** Matches MAX_NOTE_LENGTH on the API. */
export const MAX_NOTE_LENGTH = 500;
export const validateRequired = required();
export const validatePrice = [number(), minValue(1), required()];
export const validatePercentage = [
  number(),
  minValue(0),
  maxValue(1),
  required(),
];
export const validatePercentageOptional = [number(), minValue(0), maxValue(1)];
export const validateCustomPercentage = [
  number(),
  minValue(0),
  validateBelowMaxMorfySplit,
  required(),
];

export const validateCustomSplitUnits = [
  number(),
  minValue(0),
  validateInteger,
];
export const validateCustomSplitPercentage = [
  number(),
  minValue(0),
  validateBelowMaxMorfySplit,
];
export const validateEmail = [email(), required()];
export const validateOrder = [number(), minValue(1), required()];
export const validateOrderOptional = [number(), minValue(1)];
export const validateStockWithZero = [number(), minValue(0), required()];
export const validateQuantity = [number(), minValue(1), required()];
export const validateQuantityOptional = [number(), minValue(1)];
export const validatePhone = [
  required(),
  regex(PHONE_REGEX, "El formato debe ser: +5491123456789"),
];
export const cuitRegex = /^(20|23|24|25|26|27|30|33|34)\d{9}$/;
export const validateCuit = [
  regex(
    cuitRegex,
    "El CUIT/CUIL no es válido. Debe tener 11 dígitos numéricos y comenzar con un prefijo válido."
  ),
];
export const validateProductDescription = [
  required(),
  minLength(1),
  maxLength(MAX_PRODUCT_DESCRIPTION_LENGTH),
];

export const validateProductName = [required(), minLength(1), maxLength(50)];
export const validateUserNote = maxLength(MAX_NOTE_LENGTH);
export const validateMealTimes = [required(), minLength(1), maxLength(2)];
export const validateProductEventName = [minLength(1), maxLength(30)];

export const validateProductEventDates = (value, allValues) => {
  const { fromPresale, from } = allValues;
  if (fromPresale && from && new Date(from) <= new Date(fromPresale)) {
    return "La fecha debe ser posterior a la preventa";
  }
  return undefined;
};

// The API does not enforce this one, so the form is the only place that does.
export const validateProductEventEndDate = (value, allValues) => {
  const { from } = allValues;
  if (from && value && new Date(value) < new Date(from)) {
    return "La fecha de fin no puede ser anterior al día del evento";
  }
  return undefined;
};

// The generator range is form-only, so it may legitimately be left empty when the
// admin adds the rows by hand.
export const validateStockDaysRange = (value, allValues) => {
  const { stockDaysFrom } = allValues;
  if (stockDaysFrom && value && new Date(value) < new Date(stockDaysFrom)) {
    return "La fecha de fin no puede ser anterior a la de inicio";
  }
  return undefined;
};

const getCustomSplitError = ({ customMorfySplit, quantity }, dayLabel) => {
  const { quantity: units, percentage } = customMorfySplit ?? {};

  if (isEmptyValue(units) && isEmptyValue(percentage)) return undefined;

  if (isEmptyValue(percentage)) return `${dayLabel}: ingresá la comisión`;

  if (isEmptyValue(units)) return `${dayLabel}: ingresá las primeras unidades`;
  if (isEmptyValue(quantity)) return undefined;

  if (Number(units) > Number(quantity)) {
    return `${dayLabel}: las primeras unidades no pueden superar la cantidad del día`;
  }

  return undefined;
};

export const validateStockDays = (days) => {
  if (!days || days.length === 0) {
    return "Debes ingresar al menos un día con stock";
  }

  const seenDates = new Set();

  for (const [index, day] of days.entries()) {
    if (!day?.date) continue;

    const dayLabel = `Día ${index + 1}`;
    const parsedDate = new Date(day.date);

    if (Number.isNaN(parsedDate.getTime())) {
      return `${dayLabel}: la fecha no es válida`;
    }

    // Dates come from a DateInput as "YYYY-MM-DD", so comparing the day portion
    // matches how the API normalizes them.
    const date = parsedDate.toISOString().split("T")[0];

    if (seenDates.has(date)) return `${dayLabel}: la fecha está repetida`;

    seenDates.add(date);

    const customSplitError = getCustomSplitError(day, dayLabel);

    if (customSplitError) return customSplitError;
  }

  return undefined;
};

export const formatTime = (time) => {
  return time instanceof Date
    ? DateTime.fromJSDate(time)
    : DateTime.fromISO(time);
};
export const validateTimeFormat = (time) => {
  if (!time || typeof time !== "string" || time.length !== 5) return false;
  return DateTime.fromISO(time).isValid;
};

export const validateTimeOrder = (timesArray) => {
  if (!timesArray || timesArray.length === 0) return undefined; // No error if no value

  const errors = [];
  for (let i = 0; i < timesArray.length; i++) {
    const { opening, closing } = timesArray[i];

    // Convert both opening and closing to DateTime objects, handling both ISO strings and Date objects
    const currentOpening = formatTime(opening);
    const currentClosing = formatTime(closing);

    // Extract only the time portion (hour, minute) for comparison
    const currentOpeningTime = currentOpening.toFormat("HH:mm");
    const currentClosingTime = currentClosing.toFormat("HH:mm");

    const isOpeningValid = validateTimeFormat(currentOpeningTime);
    const isClosingValid = validateTimeFormat(currentClosingTime);

    if (!isOpeningValid || !isClosingValid) {
      errors[i] = {
        ...(isOpeningValid
          ? {}
          : {
              opening: "El formato debe ser HH:mm, desde 00:00 hasta 23:59",
            }),
        ...(isClosingValid
          ? {}
          : {
              closing: "El formato debe ser HH:mm, desde 00:00 hasta 23:59",
            }),
      };
    }

    if (timesArray.length === 1 || i === 0) {
      continue;
    }
    const lastClosing = formatTime(timesArray[i - 1]?.closing);
    const lastClosingTime = lastClosing.toFormat("HH:mm");

    // Ensure current closing time is before the next opening time
    if (currentOpeningTime < lastClosingTime) {
      errors[i] = {
        opening:
          "El cierre de una entrada debe ser antes del inicio de la siguiente",
      };
    }
  }
  if (errors.length > 0) {
    return errors;
  }

  return undefined; // Return undefined if no error
};

export const validateVideoStartDate = (value, allValues) => {
  if (!value && allValues.endDate) {
    return "Debes ingresar una fecha de inicio";
  }
};

export const validateServiceTypes = (value) => {
  if (!value || value.length === 0)
    return "Debes seleccionar al menos un tipo de servicio";
  return undefined;
};

export const validateTucanMenuAlias = (value, allValues) => {
  const storeId = allValues?.integrations?.tucan?.storeId;
  const deliveryEnabled = allValues?.serviceTypes?.includes(
    RESTAURANT_SERVICE_TYPES.DELIVERY
  );
  if ((storeId != null && storeId !== "") || deliveryEnabled) {
    if (!value || value === "") return "Requerido";
  }
  return undefined;
};

export const validateTucanStoreId = (value, allValues) => {
  const menuAlias = allValues?.integrations?.tucan?.menuAlias;
  const deliveryEnabled = allValues?.serviceTypes?.includes(
    RESTAURANT_SERVICE_TYPES.DELIVERY
  );
  if (menuAlias || deliveryEnabled) {
    if (value == null || value === "") return "Requerido";
  }
  return undefined;
};

export const validateDiscountCouponFunding = (value, allValues) => {
  const { fundedBy, restaurantFund, morfyFund } = allValues?.funding;
  const { type, amountValue, percentageValue } = allValues?.discount;

  const morfyFundValue = isMorfyFund(fundedBy) ? morfyFund : 0;
  const restaurantFundValue = isRestaurantFund(fundedBy) ? restaurantFund : 0;

  const totalFunding = morfyFundValue + restaurantFundValue;
  const discountValue = type === "AMOUNT" ? amountValue : percentageValue;

  if (totalFunding !== discountValue) {
    return "El descuento no puede ser distinto al monto total de la financiación";
  }

  return undefined;
};
